/**
 * Generic hook for handling data refresh based on WebSocket notifications
 * This hook listens for refresh events and triggers appropriate API calls
 */

import { useCallback, useEffect } from "react";
import { useAtom, useSetAtom } from "jotai";
import {
  allEventsListAtom,
  chatSessionsListDataAtom,
  dashboardUserCountsAtom,
  guardianListWithDetailsAtom,
  centerHeadListWithDetailsAtom,
  loggedInUserDetailsAtom,
  memberListWithDetailsAtom,
  paymentsAtom,
  personalisationByMemberIdAtom,
  traineesListWithDetailsAtom,
} from "@/app/lib/util/context.client";
import { fetchAllMembers } from "@/app/services/data_calls/members.fetch";
import { AppConstants } from "@/app/lib/util/AppConstants";
import useInitializeUserFromCookie from "@/app/components/hooks/useInitializeUserFromCookie";
import { usePathname } from "next/navigation";
import logger from "@/lib/logger";
import {
  fetchAllGuardians,
  fetchTraineeList,
  fetchAllCenterHeads,
} from "@/app/services/data_calls/users.fetch";
import { fetchAllPayments } from "@/app/services/data_calls/payments.fetch";
import { fetchAllSessionsData } from "@/app/services/data_calls/chats.fetch";
import { fetchDashboardUserCounts } from "@/app/services/data_calls/userCounts.fetch";
import { getUserDetailsApi } from "@/app/services/users.service";
import { isAccessSuspendedResponse } from "@/app/lib/util/access-suspension.util";
import { handleAccessSuspendedLogout } from "@/app/services/access-suspension.client";
import { HttpStatus } from "@/app/lib/models/ResponseFormatModel";
import { syncLoginCookieFromUserDetails } from "@/app/lib/util/utilities.client";
import {
  hasActivePlanAccess,
  hasActivePremiumSubscription,
  preferPaidAccessUser,
} from "@/app/lib/util/subscription-paywall.util";
import { getTimestampMs } from "@/app/lib/util/subscription-payment.util";
import { UserDetailsModel } from "@/app/lib/models/UserDetailsModel";

interface RefreshEventDetail {
  notification: {
    type: string;
    data?: Record<string, any>;
  };
}

export const useDataRefresh = () => {
  const currentUrl = usePathname() as string;
  const userDetailsFromCookie = useInitializeUserFromCookie(currentUrl);
  const [membersList] = useAtom(memberListWithDetailsAtom);
  const setMembersListAtom = useSetAtom(memberListWithDetailsAtom);
  const setChatSessionsAtom = useSetAtom(chatSessionsListDataAtom);
  const setTraineesListAtom = useSetAtom(traineesListWithDetailsAtom);
  const setGuardiansListAtom = useSetAtom(guardianListWithDetailsAtom);
  const setCenterHeadListAtom = useSetAtom(centerHeadListWithDetailsAtom);
  const setPaymentsAtom = useSetAtom(paymentsAtom);
  const setAllEventsAtom = useSetAtom(allEventsListAtom);
  const setPersonalisationAtom = useSetAtom(personalisationByMemberIdAtom);
  const setDashboardUserCounts = useSetAtom(dashboardUserCountsAtom);
  const setLoggedInUser = useSetAtom(loggedInUserDetailsAtom);

  const syncUserCookieFromServer = useCallback(async (): Promise<boolean> => {
    try {
      if (!userDetailsFromCookie?.email_id || !userDetailsFromCookie?.role)
        return false;

      const userDetailsResponse = await getUserDetailsApi(
        userDetailsFromCookie.email_id,
        undefined,
        undefined,
        false,
      );

      if (isAccessSuspendedResponse(userDetailsResponse)) {
        void handleAccessSuspendedLogout();
        return false;
      }

      if (
        userDetailsResponse?.status !== HttpStatus.OK ||
        !userDetailsResponse.data
      )
        return false;

      const latestUser = preferPaidAccessUser(
        userDetailsFromCookie,
        userDetailsResponse.data as UserDetailsModel,
      );
      setLoggedInUser(latestUser);
      const { updated, accountTypeChanged } =
        syncLoginCookieFromUserDetails(latestUser);
      if (!updated) return false;

      const previousPremiumEnd = getTimestampMs(
        userDetailsFromCookie.premium_end_date,
      );
      const nextPremiumEnd = getTimestampMs(latestUser.premium_end_date);
      const previousPlanEnd = getTimestampMs(
        userDetailsFromCookie.plan_end_date,
      );
      const nextPlanEnd = getTimestampMs(latestUser.plan_end_date);
      const previousIntroEnd = getTimestampMs(
        userDetailsFromCookie.introductory_expires_at,
      );
      const nextIntroEnd = getTimestampMs(latestUser.introductory_expires_at);
      const premiumEndChanged = previousPremiumEnd !== nextPremiumEnd;
      const planEndChanged = previousPlanEnd !== nextPlanEnd;
      const introEndChanged = previousIntroEnd !== nextIntroEnd;
      const tierChanged =
        (userDetailsFromCookie.plan_tier || "") !==
        (latestUser.plan_tier || "");
      const accessChanged =
        hasActivePlanAccess(latestUser) !==
        hasActivePlanAccess(userDetailsFromCookie);
      const previousBoostUntil = getTimestampMs(
        (userDetailsFromCookie as any).entitlement_boost_until,
      );
      const nextBoostUntil = getTimestampMs(
        (latestUser as any).entitlement_boost_until,
      );
      const boostChanged =
        ((userDetailsFromCookie as any).entitlement_boost_tier || "") !==
          ((latestUser as any).entitlement_boost_tier || "") ||
        previousBoostUntil !== nextBoostUntil;
      const shouldReload =
        accountTypeChanged ||
        premiumEndChanged ||
        planEndChanged ||
        introEndChanged ||
        tierChanged ||
        accessChanged ||
        boostChanged ||
        (hasActivePremiumSubscription(latestUser) &&
          !hasActivePremiumSubscription(userDetailsFromCookie));

      if (shouldReload && typeof window !== "undefined") {
        logger.info(
          "Subscription/account data changed. Reloading page for latest access.",
        );
        window.location.reload();
        return true;
      }

      return false;
    } catch (error) {
      logger.error("Error syncing user cookie from server:", error);
      return false;
    }
  }, [userDetailsFromCookie, setLoggedInUser]);

  useEffect(() => {
    if (!userDetailsFromCookie?.email_id || !userDetailsFromCookie?.role) {
      return;
    }

    const handleDataRefresh = async (
      event: CustomEvent<RefreshEventDetail>,
    ) => {
      const { notification } = event.detail;
      const { type } = notification;

      try {
        switch (type) {
          case "members_added":
          case "members_updated":
          case "members_deleted":
            logger.info("Refreshing members list due to notification:", type);
            setPersonalisationAtom(new Map());
            const refreshedMembers = await fetchAllMembers(
              userDetailsFromCookie.email_id,
              userDetailsFromCookie.role || "",
              setMembersListAtom,
            );
            logger.info(
              "Members list refreshed:",
              refreshedMembers?.length || 0,
            );
            break;

          case "chats_updated":
          case "sessions_updated":
            logger.info("Refreshing chat sessions due to notification:", type);
            await fetchAllSessionsData(
              userDetailsFromCookie.email_id,
              userDetailsFromCookie.role || "",
              membersList || new Map(),
              setChatSessionsAtom,
            );
            break;

          case "trainee_added":
          case "trainees_updated":
            logger.info(
              "Refreshing trainees/members lists due to notification:",
              type,
            );
            // Creating a trainee also seeds default contacts — refresh both lists.
            await Promise.all([
              fetchTraineeList(
                userDetailsFromCookie.email_id,
                userDetailsFromCookie.role || "",
                userDetailsFromCookie.login_session_id,
                setTraineesListAtom,
              ),
              fetchAllMembers(
                userDetailsFromCookie.email_id,
                userDetailsFromCookie.role || "",
                setMembersListAtom,
              ),
              userDetailsFromCookie.role === AppConstants.Roles.ADMIN ||
              userDetailsFromCookie.role === AppConstants.Roles.CENTER_HEAD
                ? fetchAllGuardians(
                    userDetailsFromCookie.email_id,
                    setGuardiansListAtom,
                  )
                : Promise.resolve(),
            ]);
            await fetchDashboardUserCounts(
              userDetailsFromCookie,
              setDashboardUserCounts,
            );
            await syncUserCookieFromServer();
            break;

          case "guardians_updated":
            logger.info("Refreshing guardians list due to notification:", type);
            if (
              userDetailsFromCookie.role === AppConstants.Roles.ADMIN ||
              userDetailsFromCookie.role === AppConstants.Roles.CENTER_HEAD
            ) {
              await Promise.all([
                fetchAllGuardians(
                  userDetailsFromCookie.email_id,
                  setGuardiansListAtom,
                ),
                fetchAllMembers(
                  userDetailsFromCookie.email_id,
                  userDetailsFromCookie.role || "",
                  setMembersListAtom,
                ),
                fetchTraineeList(
                  userDetailsFromCookie.email_id,
                  userDetailsFromCookie.role || "",
                  userDetailsFromCookie.login_session_id,
                  setTraineesListAtom,
                ),
              ]);
              await fetchDashboardUserCounts(
                userDetailsFromCookie,
                setDashboardUserCounts,
              );
            }
            break;

          case "center_heads_updated":
            logger.info(
              "Refreshing center heads list due to notification:",
              type,
            );
            if (userDetailsFromCookie.role === AppConstants.Roles.ADMIN) {
              await fetchAllCenterHeads(
                userDetailsFromCookie.email_id,
                setCenterHeadListAtom,
              );
            }
            break;

          case "subscription_update":
          case "payment_success":
          case "account_upgrade":
            logger.info(
              "Refreshing subscription/payments due to notification:",
              type,
            );
            // Add a small delay to ensure backend database writes (like B2B activations) are fully propagated before fetching
            await new Promise((resolve) => setTimeout(resolve, 1500));
            try {
              await fetchAllPayments(setPaymentsAtom);
            } catch (error) {
              logger.warn(
                "fetchAllPayments skipped/failed during subscription refresh",
                { error: String(error) },
              );
            }
            if (
              userDetailsFromCookie.role === AppConstants.Roles.CENTER_HEAD ||
              userDetailsFromCookie.role === AppConstants.Roles.ADMIN ||
              userDetailsFromCookie.role === AppConstants.Roles.GUARDIAN
            ) {
              await Promise.all([
                fetchTraineeList(
                  userDetailsFromCookie.email_id,
                  userDetailsFromCookie.role || "",
                  userDetailsFromCookie.login_session_id,
                  setTraineesListAtom,
                ),
                fetchAllMembers(
                  userDetailsFromCookie.email_id,
                  userDetailsFromCookie.role || "",
                  setMembersListAtom,
                ),
              ]);
            }
            await syncUserCookieFromServer();
            break;

          case "profile_update":
          case "user_update":
            logger.info(
              "Refreshing profile-linked lists due to notification:",
              type,
            );
            await Promise.all([
              fetchTraineeList(
                userDetailsFromCookie.email_id,
                userDetailsFromCookie.role || "",
                userDetailsFromCookie.login_session_id,
                setTraineesListAtom,
              ),
              fetchAllMembers(
                userDetailsFromCookie.email_id,
                userDetailsFromCookie.role || "",
                setMembersListAtom,
              ),
              userDetailsFromCookie.role === AppConstants.Roles.ADMIN ||
              userDetailsFromCookie.role === AppConstants.Roles.CENTER_HEAD
                ? fetchAllGuardians(
                    userDetailsFromCookie.email_id,
                    setGuardiansListAtom,
                  )
                : Promise.resolve(),
            ]);
            await syncUserCookieFromServer();
            break;

          case "events_added":
          case "events_updated":
          case "events_deleted":
            logger.info("Invalidating events cache due to notification:", type);
            setAllEventsAtom([]);
            break;

          default:
            logger.info("Unknown refresh type:", type);
        }
      } catch (error) {
        logger.error(`Error refreshing data for type ${type}:`, error);
      }
    };

    window.addEventListener(
      "data-refresh-requested",
      handleDataRefresh as EventListener,
    );
    window.addEventListener(
      "members-refresh-requested",
      handleDataRefresh as EventListener,
    );

    return () => {
      window.removeEventListener(
        "data-refresh-requested",
        handleDataRefresh as EventListener,
      );
      window.removeEventListener(
        "members-refresh-requested",
        handleDataRefresh as EventListener,
      );
    };
  }, [
    userDetailsFromCookie,
    membersList,
    setMembersListAtom,
    setChatSessionsAtom,
    setTraineesListAtom,
    setGuardiansListAtom,
    setCenterHeadListAtom,
    setPaymentsAtom,
    setAllEventsAtom,
    setPersonalisationAtom,
    setDashboardUserCounts,
    syncUserCookieFromServer,
  ]);
};
